/**
 * Public Trial Checkout — /checkout?code=<token>
 *
 * Step 2 of the two-link self-serve subscription flow. The user must already
 * be authenticated (they registered via /subscribe). When the token is valid
 * and the user is logged in, this page immediately calls Stripe Checkout for
 * the Pro plan and forwards the browser to the hosted Stripe URL.
 *
 * This is a hand-off screen, not a checkout form — Stripe owns the card
 * collection UI on its own page. We surface:
 *   - A step indicator so the prospect knows where they are.
 *   - An order summary matching what Stripe will charge (so they can spot
 *     discrepancies before committing a card).
 *   - A status ledger that mirrors the async steps (validate → session →
 *     redirect).
 *   - A "don't close this window" hint.
 *
 * Industry design system (auth grammar): centered blueprint card on the
 * light ground with the Z plate + wordmark, a square hairline progress
 * track with accent fill, order summary as a hairline table with dashed
 * dividers and status rows with square glyph plates (accent = done,
 * ink = error). No navy fill, no monospace, no circular ring.
 *
 * Error states:
 *   - Missing/expired token → friendly error + link back to /subscribe.
 *   - Not authenticated → redirect to /subscribe?code=... so the prospect
 *     can register first.
 *   - Stripe call failure → surfaced inline with a retry CTA.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { LockOutlined as LockIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/auth';
import { billingService } from '../services/billing';
import publicTrialService from '../services/publicTrial';
import SubscriptionStepper from '../components/billing/SubscriptionStepper';

const PUBLIC_CHECKOUT_HANDOFF_KEY = 'aviatto:publicCheckoutHandoff';

const HEADING = 'var(--font-heading)';
const INK = 'var(--color-neutral-900)';
const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const ACC8 = 'var(--color-accent-800)';
const MUTED55 = 'color-mix(in srgb, var(--color-text) 55%, transparent)';
const MUTED58 = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const MUTED72 = 'color-mix(in srgb, var(--color-text) 72%, transparent)';
const TINT = 'color-mix(in srgb, var(--color-accent) 8%, transparent)';

function buildSubscribeUrl(code) {
  const safeCode = encodeURIComponent(String(code || '').trim());
  return safeCode ? `/subscribe?code=${safeCode}` : '/subscribe';
}

function nowClock() {
  const d = new Date();
  return d.toLocaleTimeString('pt-BR', { hour12: false });
}

const STAGE_LABELS = {
  idle: 'Preparando pagamento...',
  validating: 'Validando seu link...',
  starting: 'Abrindo checkout no Stripe...',
  redirecting: 'Redirecionando para o Stripe...',
  error: 'Não foi possível conectar ao Stripe.',
};

// Square hairline progress: accent fill share per async stage.
const STAGE_PROGRESS = {
  idle: 12,
  validating: 40,
  starting: 72,
  redirecting: 96,
  error: 100,
};

export default function PublicTrialCheckout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const code = (params.get('code') || '').trim();

  const [stage, setStage] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [stripeUrl, setStripeUrl] = useState('');
  const [traceLog, setTraceLog] = useState([]);
  // Guard against React 18 strict-mode double effect invocation creating two
  // Stripe sessions in development.
  const startedRef = useRef(false);

  const appendTrace = (tone, message) => {
    setTraceLog((prev) => [...prev, { tone, message, at: nowClock() }]);
  };

  useEffect(() => {
    if (!code) {
      setStage('error');
      setErrorMessage(
        'Link de pagamento invalido — o codigo de convite esta ausente. Solicite um novo link a equipe Aviato.'
      );
      return undefined;
    }

    if (!authService.isAuthenticated()) {
      navigate(buildSubscribeUrl(code), { replace: true });
      return undefined;
    }

    if (startedRef.current) return undefined;
    startedRef.current = true;

    let cancelled = false;
    const startStripe = async () => {
      setStage('validating');
      appendTrace('done', 'Conta autenticada · sessão JWT ativa');
      appendTrace('pending', 'Validando link de assinatura');
      try {
        const validation = await publicTrialService.validate(code);
        if (cancelled) return;
        if (!validation?.valid) {
          setStage('error');
          setErrorMessage(
            'Este link de pagamento expirou ou nao e valido. Solicite um novo link a equipe Aviato.'
          );
          appendTrace('error', 'Link inválido ou expirado');
          return;
        }
        appendTrace('done', 'Link válido · token de checkout gerado');

        setStage('starting');
        appendTrace('pending', 'Criando sessão de checkout no Stripe');
        const data = await billingService.createCheckoutSession({
          plan_tier: 'pro',
          checkout_access_token: code,
          billing_email: user?.email,
          full_name: user?.full_name,
          company_name: user?.company_name,
        });
        if (cancelled) return;

        if (data?.url) {
          setStripeUrl(data.url);
          setStage('redirecting');
          appendTrace('done', 'Sessão Stripe criada · redirecionando');
          try {
            sessionStorage.setItem(PUBLIC_CHECKOUT_HANDOFF_KEY, '1');
          } catch {
            // Best-effort; BillingSuccess also accepts an explicit query flag.
          }
          window.location.href = data.url;
          return;
        }

        setStage('error');
        const friendly =
          [data?.error, data?.detail].filter(Boolean).join(': ') ||
          'Nao foi possivel abrir o checkout do Stripe agora.';
        setErrorMessage(friendly);
        // Trace log uses a generic tag so the detailed message only lives
        // in the error box (keeps findByText in tests unambiguous).
        appendTrace('error', 'Falha ao abrir a sessão Stripe');
      } catch (err) {
        if (cancelled) return;
        setStage('error');
        const data = err?.response?.data || {};
        const friendly =
          data.error ||
          data.detail ||
          'Nao foi possivel abrir o checkout do Stripe agora. Tente novamente em instantes.';
        setErrorMessage(friendly);
        appendTrace('error', 'Falha na conexão com o Stripe');
      }
    };

    startStripe();
    return () => {
      cancelled = true;
    };
  }, [code, navigate, user]);

  const orderSummary = useMemo(
    () => [
      ['Plano', 'Zulbot Pro · Mensal'],
      ['Operação', user?.company_name || '—'],
      ['Frequência', 'a cada 30 dias'],
      ['Total hoje', 'R$ 500,00 BRL', true],
    ],
    [user]
  );

  const isLoading = stage === 'idle' || stage === 'validating' || stage === 'starting' || stage === 'redirecting';

  const traceGlyph = { done: '✓', pending: '→', error: '!' };

  return (
    <Box
      className="zb-industry"
      sx={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: '32px 24px 44px',
        boxSizing: 'border-box',
      }}
    >
      {/* Z plate + wordmark */}
      <Box
        component="a"
        href="/login"
        sx={{ display: 'flex', alignItems: 'center', gap: '9px', textDecoration: 'none', color: 'var(--color-text)' }}
      >
        <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1.5px solid var(--color-text)', fontFamily: HEADING, fontSize: 15, fontWeight: 600 }}>
          Z
        </Box>
        <Box component="span" sx={{ fontFamily: HEADING, fontSize: 19, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Zulbot
        </Box>
      </Box>

      <Box
        className="blueprint"
        sx={{ width: '100%', maxWidth: 560, mt: 3, p: '26px', background: 'transparent', boxShadow: 'var(--ind-shadow-md)' }}
      >
        <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Box component="span" sx={{ fontFamily: HEADING, fontSize: 10, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: ACC7 }}>
            Handoff · Stripe
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED55 }}>
            Secure · TLS 1.3
          </Box>
        </Box>

        <Typography
          component="h1"
          sx={{ m: '8px 0 0', fontFamily: HEADING, fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase' }}
        >
          Pagamento da assinatura
        </Typography>
        <Typography sx={{ m: '7px 0 0', fontSize: 13.5, lineHeight: 1.55, color: MUTED72 }}>
          Passo 2 de 2 — Estamos abrindo o checkout seguro do Stripe para concluir
          a sua assinatura do plano{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Pro
          </Box>
          .
        </Typography>

        <SubscriptionStepper activeIndex={1} sx={{ mt: '18px' }} />

        {/* Square hairline progress track with accent fill */}
        <Box sx={{ mt: '18px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '5px' }}>
            <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: stage === 'error' ? INK : ACC7 }}>
              {stage === 'error' ? 'VERIFIQUE O LINK' : 'VALIDANDO LINK SEGURO'}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED55 }}>
              {STAGE_LABELS[stage]}
            </Box>
          </Box>
          <Box sx={{ height: 6, background: 'var(--color-neutral-200)' }}>
            <Box
              sx={{
                height: '100%',
                width: `${STAGE_PROGRESS[stage] ?? 0}%`,
                background: stage === 'error' ? INK : ACC,
                transition: 'width 300ms ease',
              }}
            />
          </Box>
        </Box>

        {/* Order summary — hairline table with dashed row dividers */}
        <Box sx={{ mt: '18px', border: '1px solid var(--color-divider)' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              p: '9px 13px',
              borderBottom: '1px dashed var(--color-neutral-300)',
            }}
          >
            <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTED58 }}>
              Resumo · Order
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED55 }}>
              AVT · PRO · BRL
            </Box>
          </Box>
          {orderSummary.map(([key, value, bold]) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                p: '8px 13px',
                borderBottom: '1px dashed var(--color-neutral-200)',
                '&:last-of-type': { borderBottom: 'none' },
                ...(bold ? { borderTop: '1px dashed var(--color-neutral-300)' } : {}),
              }}
            >
              <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED58 }}>
                {key}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: HEADING,
                  fontSize: bold ? 15 : 13,
                  fontWeight: 600,
                  textAlign: 'right',
                }}
              >
                {value}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Status ledger — stacked rows with square glyph plates */}
        {traceLog.length > 0 && (
          <Box sx={{ mt: '14px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {traceLog.map((line, i) => {
              const isError = line.tone === 'error';
              const isDone = line.tone === 'done';
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      display: 'grid',
                      placeItems: 'center',
                      width: 16,
                      height: 15,
                      fontFamily: HEADING,
                      fontSize: 9,
                      fontWeight: 600,
                      border: `1px solid ${isError ? INK : isDone ? ACC : 'var(--color-divider)'}`,
                      background: isDone ? TINT : 'transparent',
                      color: isError ? INK : isDone ? ACC7 : MUTED58,
                    }}
                  >
                    {traceGlyph[line.tone] || '·'}
                  </Box>
                  <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED55 }}>
                    {line.at}
                  </Box>
                  <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.4, color: isError ? INK : 'var(--color-text)' }}>
                    {line.message}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {stage === 'error' && (
          <Box sx={{ mt: '14px', p: '9px 11px', border: `1px solid ${INK}`, fontSize: 12.5, lineHeight: 1.45, color: INK }}>
            {errorMessage}
          </Box>
        )}

        {stage === 'error' && (
          <Box sx={{ display: 'flex', gap: '9px', mt: '16px', flexWrap: 'wrap' }}>
            <Box
              component={RouterLink}
              to={buildSubscribeUrl(code)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 38,
                px: '14px',
                border: '1px solid var(--color-divider)',
                color: 'var(--color-text)',
                fontFamily: HEADING,
                fontSize: '11.5px',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                '&:hover': { borderColor: 'rgba(29,31,32,0.45)' },
              }}
            >
              Voltar para o cadastro
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box
              component="a"
              href="mailto:support@zulbot.ai?subject=Link%20de%20assinatura"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 38,
                px: '16px',
                border: `1px solid ${ACC}`,
                background: ACC,
                color: 'var(--color-bg)',
                fontFamily: HEADING,
                fontSize: '11.5px',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
              }}
            >
              Falar com o time Zulbot
            </Box>
          </Box>
        )}

        {stage === 'redirecting' && stripeUrl && (
          <Typography sx={{ display: 'block', textAlign: 'center', mt: '14px', fontSize: 12, color: MUTED58 }}>
            Se nada acontecer em alguns segundos,{' '}
            <Box component="a" href={stripeUrl} sx={{ color: ACC7, fontWeight: 600, textDecoration: 'none', '&:hover': { color: ACC8 } }}>
              clique aqui para abrir o Stripe
            </Box>
            .
          </Typography>
        )}

        {isLoading && (
          <Typography sx={{ textAlign: 'center', mt: '14px', fontSize: 12, color: MUTED58, lineHeight: 1.5 }}>
            Não feche esta janela — você será redirecionado em instantes.
          </Typography>
        )}

        {/* Footer: PCI note + card networks */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            mt: '20px',
            pt: '15px',
            borderTop: '1px dashed var(--color-neutral-300)',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', color: MUTED55 }}>
            <LockIcon sx={{ fontSize: 14 }} />
            <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Stripe · PCI-DSS Level 1
            </Box>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', gap: '10px' }}>
            {['VISA', 'MASTERCARD', 'AMEX', 'PIX'].map((brand) => (
              <Box key={brand} component="span" sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED55 }}>
                {brand}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
